import { useEffect, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import NotesWorkspace from 'components/NotesWorkspace'
import type { NoteImageRecord, NotesSurfaceAdapter } from 'components/NotesWorkspace'
import {
	createSiteNote,
	deleteSiteNote,
	getSiteNotes,
	updateSiteNote,
} from 'slices/siteNoteSlice'
import {
	createSiteImage,
	getSiteImages,
	updateSiteImage,
	deleteSiteImage,
} from 'slices/siteImageSlice'
import { prepareNoteHtml } from 'utils/noteUpload'
import htmlToPlainText from 'utils/htmlToPlainText'
import { removeImageFromNoteHtml } from 'utils/noteImages'
import type { SiteOutletContext } from '../Sites_ShowCN'

/**
 * Notes for one evaluation site, in the two-column workspace layout.
 *
 * Runs alongside SiteNotes (the Notes tab) rather than replacing it, so the two
 * layouts can be compared on the same data. Both read the same slices, so a
 * note written on either tab shows up on the other.
 */
const SiteNotes = () => {
	const { site, siteId } = useOutletContext<SiteOutletContext>()
	const dispatch = useDispatch()

	const userInfo = useSelector((state: any) => state.userLogin.userInfo)
	const { entities, ids, isLoading, error } = useSelector((state: any) => state.siteNotes)
	const siteImages = useSelector((state: any) => state.siteImages)

	useEffect(() => {
		dispatch(getSiteNotes(siteId) as any)
		// The gallery rows back the review dialog inside each note: the note HTML
		// carries only URLs, so the rows are what make an image editable.
		dispatch(getSiteImages(siteId) as any)
	}, [dispatch, siteId])

	const notes = useMemo(
		() => ids.map((id: string) => entities[id]).filter(Boolean),
		[ids, entities],
	)

	const imageRecords = useMemo<NoteImageRecord[]>(
		() => siteImages.ids.map((id: string) => siteImages.entities[id]).filter(Boolean),
		[siteImages.ids, siteImages.entities],
	)

	const adapter = useMemo<NotesSurfaceAdapter>(
		() => ({
			key: 'site',
			title: 'Notes',
			subtitle: site?.name || 'this site',
			supportsReplies: true,
			notes,
			imageRecords,
			isLoading,
			error,
			currentUserId: userInfo?._id,

			createNote: async ({ contents, parentid, libraryImages }) => {
				// The composer hands back one body per note: several when the
				// author chose a separate note per image, one otherwise.
				let wroteImages = 0

				for (const [position, body] of contents.entries()) {
					const { html, uploaded } = await prepareNoteHtml(body, siteId, {
						folder: `mdt/sites/${siteId}`,
						// Library rows belong to the submission, not to each body,
						// so only the first pass reports them. Otherwise N notes
						// would write N copies of the same gallery row.
						libraryImages: position === 0 ? libraryImages : [],
					})

					await dispatch(
						createSiteNote({ siteid: siteId, note: html, parentid: parentid || null }) as any,
					)

					// Mirror each embedded image into the gallery, so a picture added
					// through a note is not invisible on the Images tab.
					const plainNote = htmlToPlainText(body)
					for (const image of uploaded) {
						await dispatch(
							createSiteImage({
								siteid: siteId,
								uuid: image.public_id,
								name: image.original_filename,
								size: image.bytes,
								url: image.secure_url,
								source: 'cloudinary',
								type: image.format,
								note: plainNote,
							}) as any,
						)
						wroteImages += 1
					}
				}

				// Refetched once at the end rather than per note.
				await dispatch(getSiteNotes(siteId) as any)
				if (wroteImages > 0) await dispatch(getSiteImages(siteId) as any)
			},

			updateImage: async image => {
				await dispatch(updateSiteImage(image) as any)
			},

			/**
			 * Removing an image takes it out of both places it lives: the note's
			 * HTML and the gallery. Dropping only the row would leave the note
			 * rendering a picture that no longer exists.
			 */
			removeImage: async ({ image, url, noteId, noteBody }) => {
				const nextHtml = removeImageFromNoteHtml(noteBody, url)
				if (noteId && nextHtml !== noteBody) {
					await dispatch(updateSiteNote({ _id: noteId, note: nextHtml }) as any)
				}
				await dispatch(deleteSiteImage(image._id) as any)
				await dispatch(getSiteNotes(siteId) as any)
				await dispatch(getSiteImages(siteId) as any)
			},

			/**
			 * Show-only; the server enforces in `noteDeleteGuards`.
			 *
			 * `assertSiteOwner` runs before the guard on every site-note route, so a
			 * non-owner cannot even list these notes — anyone reading this feed is
			 * already the site's owner, and the guard's "author OR asset owner" test
			 * is satisfied for every note here. The author check is kept anyway
			 * rather than returning a bare `true`, so that if site notes ever become
			 * visible to partners this does not quietly start offering delete on
			 * everybody's notes.
			 */
			canDeleteNote: note => {
				const viewerId = userInfo?._id
				if (!viewerId) return false
				const authorId = note.userid?._id ?? note.userid
				if (authorId && String(authorId) === String(viewerId)) return true
				const ownerId = site?.owner?._id ?? site?.owner
				return Boolean(ownerId && String(ownerId) === String(viewerId))
			},

			deleteNote: async note => {
				const action: any = await dispatch(deleteSiteNote(note._id) as any)
				if (action?.meta?.requestStatus === 'rejected') {
					return String(action.payload ?? 'Could not delete this note.')
				}
				// The slice dropped the note; this is for the image rows the server
				// soft-deleted with it.
				await dispatch(getSiteImages(siteId) as any)
				return null
			},
		}),
		[
			dispatch,
			site?.name,
			site?.owner,
			siteId,
			notes,
			imageRecords,
			isLoading,
			error,
			userInfo?._id,
		],
	)

	return <NotesWorkspace adapter={adapter} />
}

export default SiteNotes
